/**
 * HTTP clients for the v2 Data Studio asset APIs.
 *
 * Each function branches once on the feature's mock flag and otherwise calls
 * `apiService`; both branches return the same wire shape, and mapping to the
 * UI contracts happens in `dataStudioColumnProfileMappers.ts`. Errors are
 * deliberately propagated so React Query call sites can surface the backend's
 * own message through `getApiErrorMessage`.
 */
import { API_ENDPOINTS, apiService } from "../api";
import { isMockApiFlag } from "../apiMode";
import { TABULAR_CONNECTOR_ASSET_TYPES } from "../connectors/connectorAsset.types";
import {
  fetchMockAsset,
  fetchMockAssetColumns,
  fetchMockAssetRows,
  fetchMockAssetSearch,
} from "./dataStudioAssets.mock";
import {
  mapAssetSearchResponse,
  mapColumnsPageResponse,
  mapRowsResponse,
  mapTabularAssetProfile,
} from "./dataStudioColumnProfileMappers";
import { getLiveSampleColumns, getLiveSampleRows } from "./dataStudioLiveSampleApi";
import { COLUMN_INCLUDE_ALL } from "./dataStudioAssets.types";
import type {
  AssetSearchListResponse,
  ColumnListResponse,
  ListAssetColumnsQuery,
  ListAssetRowsQuery,
  RowsResponse,
  SearchAssetsQuery,
  TabularAsset,
} from "./dataStudioAssets.types";
import type {
  GetDataStudioTableColumnsRequest,
  GetDataStudioTableColumnsResponse,
  GetDataStudioTableProfileRequest,
  GetDataStudioTableProfileResponse,
  GetDataStudioTableRowsRequest,
  GetDataStudioTableRowsResponse,
  ListDataStudioTablesRequest,
  ListDataStudioTablesResponse,
} from "./dataStudioTables.types";

const isMockDataStudioApiEnabled = (): boolean =>
  isMockApiFlag(process.env.REACT_APP_DATA_STUDIO_API);

/**
 * Serializes a query for axios.
 *
 * Array parameters are comma-joined because the spec declares them
 * `style: form, explode: false`; axios' default would emit `asset_type[]=a&...`
 * which the backend does not parse. Empty and undefined values are dropped so
 * a blank search box does not send `search=`.
 */
export const toQueryParams = (
  query: object,
): Record<string, string | number | boolean> => {
  const params: Record<string, string | number | boolean> = {};

  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (Array.isArray(value)) {
      if (value.length === 0) return;
      params[key] = value.join(",");
      return;
    }
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed) params[key] = trimmed;
      return;
    }
    params[key] = value as string | number | boolean;
  });

  return params;
};

// ---------------------------------------------------------------------------
// A. Asset search
// ---------------------------------------------------------------------------

/**
 * Translates the panel's request into the search API's query.
 *
 * Defaults to the three tabular asset types: Data Studio can only profile a
 * table, view, or sheet, so an unfiltered search would offer assets that the
 * columns and rows endpoints answer with a 400.
 */
export const toSearchAssetsQuery = (
  request: ListDataStudioTablesRequest,
): SearchAssetsQuery => ({
  asset_type: request.assetTypes ?? [...TABULAR_CONNECTOR_ASSET_TYPES],
  connection_id: request.connectionIds,
  search: request.search?.trim() || undefined,
  limit: request.limit,
  offset: request.offset,
  sort_by: "name",
  sort_order: request.sortOrder ?? "asc",
});

export const searchDataStudioAssets = async (
  request: ListDataStudioTablesRequest = {},
): Promise<ListDataStudioTablesResponse> => {
  const query = toSearchAssetsQuery(request);

  const response = isMockDataStudioApiEnabled()
    ? await fetchMockAssetSearch(query)
    : await apiService.get<AssetSearchListResponse>(
        API_ENDPOINTS.dataStudio.assetSearch,
        toQueryParams(query),
      );

  return mapAssetSearchResponse(response);
};

// ---------------------------------------------------------------------------
// B. Asset
// ---------------------------------------------------------------------------

export const getDataStudioAsset = async (
  connectionId: string,
  assetId: string,
): Promise<TabularAsset> => {
  if (isMockDataStudioApiEnabled()) {
    return fetchMockAsset(connectionId, assetId);
  }

  return apiService.get<TabularAsset>(
    API_ENDPOINTS.dataStudio.asset(connectionId, assetId),
  );
};

// ---------------------------------------------------------------------------
// C. Columns
// ---------------------------------------------------------------------------

export const listDataStudioAssetColumns = async (
  connectionId: string,
  assetId: string,
  query: ListAssetColumnsQuery = {},
): Promise<ColumnListResponse> => {
  if (isMockDataStudioApiEnabled()) {
    return fetchMockAssetColumns(connectionId, assetId, query);
  }

  return apiService.get<ColumnListResponse>(
    API_ENDPOINTS.dataStudio.assetColumns(connectionId, assetId),
    toQueryParams(query),
  );
};

/**
 * Column-page size for lazy, scroll-driven column loading — kept equal to
 * `DATA_STUDIO_ROW_PAGE_SIZE` below rather than the endpoint's own 200-row
 * ceiling: the two dimensions page independently, so there is no contract
 * reason for them to match, but a small shared size is what makes both the
 * vertical and horizontal infinite scroll reachable while testing against a
 * fixture table without thousands of rows or columns.
 */
export const DATA_STUDIO_COLUMN_PAGE_SIZE = 10;

/**
 * One page of columns, mapped for the profile grid's horizontal infinite
 * scroll — the column-dimension counterpart of `getDataStudioTableRows`.
 */
export const getDataStudioTableColumns = async ({
  connectionId,
  assetId,
  liveSampleSlug,
  offset = 0,
  limit = DATA_STUDIO_COLUMN_PAGE_SIZE,
}: GetDataStudioTableColumnsRequest): Promise<GetDataStudioTableColumnsResponse> => {
  if (liveSampleSlug) return getLiveSampleColumns({ slug: liveSampleSlug, connectionId, assetId });
  const response = await listDataStudioAssetColumns(connectionId, assetId, {
    limit,
    offset,
    include: [...COLUMN_INCLUDE_ALL],
  });

  return mapColumnsPageResponse(response);
};

// ---------------------------------------------------------------------------
// Composed profile (B + C)
// ---------------------------------------------------------------------------

/**
 * The dataset header, from the asset endpoint alone. Columns are no longer
 * fetched here: they used to be exhausted up front (every page, following
 * `has_next`) so the grid could render its full header row on first paint,
 * but that defeated the columns endpoint's own pagination for a wide asset
 * and gave the horizontal scroll nothing left to fetch. `useDataStudioTableProfileColumns`
 * now streams columns independently, the same way rows already stream via
 * `useDataStudioTableProfileRows` — `DataStudioPage` composes the two.
 */
export const getDataStudioTableProfile = async ({
  connectionId,
  assetId,
}: GetDataStudioTableProfileRequest): Promise<GetDataStudioTableProfileResponse> => {
  if (process.env.NODE_ENV !== "production" && isMockDataStudioApiEnabled()) {
    console.log(
      `[MOCK] getDataStudioTableProfile — request connectionId=${connectionId}, assetId=${assetId}`,
    );
  }

  const asset = await getDataStudioAsset(connectionId, assetId);

  const profile = mapTabularAssetProfile(asset, []);

  if (process.env.NODE_ENV !== "production" && isMockDataStudioApiEnabled()) {
    console.log(
      `[MOCK] getDataStudioTableProfile — response rowCount=${profile.table.rowCount ?? "null"}, columnCount=${profile.columns.length}, tableQuality=${profile.table.quality?.score ?? "null"}`,
    );
  }

  return profile;
};

// ---------------------------------------------------------------------------
// E. Rows
// ---------------------------------------------------------------------------

export const getDataStudioAssetRows = async (
  connectionId: string,
  assetId: string,
  query: ListAssetRowsQuery = {},
): Promise<RowsResponse> => {
  if (isMockDataStudioApiEnabled()) {
    return fetchMockAssetRows(connectionId, assetId, query);
  }

  return apiService.get<RowsResponse>(
    API_ENDPOINTS.dataStudio.assetRows(connectionId, assetId),
    toQueryParams(query),
  );
};

/** Preview-row page size. Below the endpoint's 1000 ceiling — the grid reveals
 * rows in small batches, so a large page would only add latency. */
export const DATA_STUDIO_ROW_PAGE_SIZE = 10;

export const getDataStudioTableRows = async ({
  connectionId,
  assetId,
  liveSampleSlug,
  offset = 0,
  limit = DATA_STUDIO_ROW_PAGE_SIZE,
  columnIds,
  columnOffset,
  columnLimit,
}: GetDataStudioTableRowsRequest): Promise<GetDataStudioTableRowsResponse> => {
  if (liveSampleSlug) return getLiveSampleRows({ slug: liveSampleSlug, connectionId, assetId, offset, limit });
  if (process.env.NODE_ENV !== "production" && isMockDataStudioApiEnabled()) {
    console.log(
      `[MOCK] getDataStudioTableRows — request offset=${offset}, limit=${limit}, columnOffset=${columnOffset ?? "default"}, columnLimit=${columnLimit ?? "default"}`,
    );
  }

  const response = await getDataStudioAssetRows(connectionId, assetId, {
    limit,
    offset,
    ...(columnIds?.length
      ? { column_ids: columnIds }
      : {
          ...(columnOffset !== undefined ? { column_offset: columnOffset } : {}),
          ...(columnLimit !== undefined ? { column_limit: columnLimit } : {}),
        }),
  });

  const mapped = mapRowsResponse(response);

  if (process.env.NODE_ENV !== "production" && isMockDataStudioApiEnabled()) {
    const { total, nextOffset, hasMore, previousOffset, hasPrevious } =
      mapped.rowPageInfo;
    console.log(
      `[MOCK] getDataStudioTableRows — response rows=${mapped.rows.length}, rowPageInfo={total=${total}, nextOffset=${nextOffset ?? "null"}, hasMore=${hasMore}, previousOffset=${previousOffset ?? "null"}, hasPrevious=${hasPrevious}}`,
    );
  }

  return mapped;
};
